import {
  cn,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@giyus/ui';

/** Cores da série vêm dos tokens `--chart-1…5` (claro/escuro). Classes estáticas para o Tailwind enxergá-las. */
const SERIES_FILL = [
  'fill-chart-1',
  'fill-chart-2',
  'fill-chart-3',
  'fill-chart-4',
  'fill-chart-5',
] as const;
const SERIES_STROKE = [
  'stroke-chart-1',
  'stroke-chart-2',
  'stroke-chart-3',
  'stroke-chart-4',
  'stroke-chart-5',
] as const;
const SERIES_BG = ['bg-chart-1', 'bg-chart-2', 'bg-chart-3', 'bg-chart-4', 'bg-chart-5'] as const;

export interface BarChartItem {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  /** Meta/total da barra (ex.: meta de recrutamento). Sem ele, a escala usa o maior valor da lista. */
  readonly target?: number;
}

export interface BarChartProps {
  readonly items: readonly BarChartItem[];
  /** Nome acessível do gráfico (ex.: "Recrutamento por estudo"). */
  readonly label: string;
  /** Formata o texto à direita da barra (i18n pelo chamador). Padrão: "valor" ou "valor / meta". */
  readonly formatValue?: (item: BarChartItem) => string;
  readonly className?: string;
}

export function percentOf(value: number, total: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) {
    return 0;
  }
  return Math.min(100, Math.max(0, (value / total) * 100));
}

function defaultFormat(item: BarChartItem): string {
  return item.target === undefined
    ? String(item.value)
    : `${String(item.value)} / ${String(item.target)}`;
}

/**
 * Barras horizontais. Os números ficam em TEXTO em cada linha (a lista é a alternativa em tabela do gráfico); a barra SVG é
 * decorativa (`aria-hidden`) e usa só atributos de apresentação (sem `style=`, DS-02).
 */
export function BarChart({ items, label, formatValue = defaultFormat, className }: BarChartProps) {
  const scaleMax = Math.max(0, ...items.map((item) => item.target ?? item.value));
  return (
    <div data-slot="bar-chart">
      <ul aria-label={label} className={cn('flex flex-col gap-3', className)}>
        {items.map((item, index) => {
          const total = item.target ?? scaleMax;
          const width = percentOf(item.value, total);
          const trackWidth = item.target === undefined ? 100 : percentOf(item.target, scaleMax);
          return (
            <li key={item.id} className="flex flex-col gap-1">
              <div className="flex items-baseline justify-between gap-3 text-body-sm">
                <span className="min-w-0 truncate text-foreground-secondary">{item.label}</span>
                <span className="shrink-0 font-data font-medium text-heading">
                  {formatValue(item)}
                </span>
              </div>
              <div role="img" aria-label={`${item.label}: ${formatValue(item)}`}>
                <svg
                  aria-hidden="true"
                  focusable="false"
                  viewBox="0 0 100 6"
                  preserveAspectRatio="none"
                  className="h-2 w-full"
                >
                  <rect x="0" y="0" width={trackWidth} height="6" rx="3" className="fill-muted" />
                  <rect
                    x="0"
                    y="0"
                    width={(width / 100) * trackWidth}
                    height="6"
                    rx="3"
                    className={SERIES_FILL[index % SERIES_FILL.length] ?? 'fill-chart-1'}
                  />
                </svg>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="sr-only">
        <Table className="sr-only">
          <TableCaption>{label}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Item</TableHead>
              <TableHead scope="col">Value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableHead scope="row">{item.label}</TableHead>
                <TableCell>{formatValue(item)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export interface DonutSegment {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  /** Índice estável da cor (0–4). Sem ele usa a posição; informe-o quando fatias com zero forem omitidas. */
  readonly colorIndex?: number;
}

export interface DonutChartProps {
  readonly segments: readonly DonutSegment[];
  /** Nome acessível do gráfico (ex.: "Estudos por fase"). */
  readonly label: string;
  /** Rótulo do total no centro (ex.: "estudos"). */
  readonly totalLabel: string;
  /** Formata o texto de cada item da legenda (i18n pelo chamador). */
  readonly formatSegment?: (segment: DonutSegment, percent: number) => string;
  readonly className?: string;
}

const CIRCUMFERENCE_RADIUS = 15.9155; // circunferência = 100 → dasharray em porcentagem

function defaultSegment(segment: DonutSegment, percent: number): string {
  return `${String(segment.value)} (${String(Math.round(percent))}%)`;
}

/** Rosca de composição. Legenda em texto (rótulo + valor + %) é a alternativa em tabela; o SVG é decorativo. */
export function DonutChart({
  segments,
  label,
  totalLabel,
  formatSegment = defaultSegment,
  className,
}: DonutChartProps) {
  const rawTotal = segments.reduce(
    (sum, segment) => sum + Math.max(0, Number.isFinite(segment.value) ? segment.value : 0),
    0,
  );
  const total = Number.isFinite(rawTotal) ? rawTotal : 0;
  let offset = 0;
  return (
    <div
      data-slot="donut-chart"
      role="group"
      aria-label={label}
      className={cn('flex flex-wrap items-center gap-6', className)}
    >
      <div role="img" aria-label={`${label}: ${String(total)} ${totalLabel}`} className="shrink-0">
        <svg
          aria-hidden="true"
          focusable="false"
          viewBox="0 0 42 42"
          className="size-36 -rotate-90"
        >
          <circle
            cx="21"
            cy="21"
            r={CIRCUMFERENCE_RADIUS}
            fill="none"
            strokeWidth="6"
            className="stroke-muted"
          />
          {total > 0
            ? segments.map((segment, index) => {
                const percent = percentOf(segment.value, total);
                const start = offset;
                offset += percent;
                if (percent === 0) {
                  return null;
                }
                return (
                  <circle
                    key={segment.id}
                    cx="21"
                    cy="21"
                    r={CIRCUMFERENCE_RADIUS}
                    fill="none"
                    strokeWidth="6"
                    strokeDasharray={`${String(Math.max(0, percent - 0.6))} ${String(100 - Math.max(0, percent - 0.6))}`}
                    strokeDashoffset={String(-start)}
                    className={
                      SERIES_STROKE[(segment.colorIndex ?? index) % SERIES_STROKE.length] ??
                      'stroke-chart-1'
                    }
                  />
                );
              })
            : null}
        </svg>
      </div>
      <div className="sr-only">
        <Table className="sr-only">
          <TableCaption>{label}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Segment</TableHead>
              <TableHead scope="col">Share</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {segments.map((segment) => (
              <TableRow key={segment.id}>
                <TableHead scope="row">{segment.label}</TableHead>
                <TableCell>{formatSegment(segment, percentOf(segment.value, total))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className="text-body-sm text-muted-foreground">
          <span className="font-data text-headline font-semibold text-heading">{total}</span>{' '}
          {totalLabel}
        </p>
        <ul className="flex flex-col gap-1.5">
          {segments.map((segment, index) => (
            <li key={segment.id} className="flex items-center gap-2 text-body-sm">
              <span
                aria-hidden="true"
                className={cn(
                  'size-3 shrink-0 rounded-sm',
                  SERIES_BG[(segment.colorIndex ?? index) % SERIES_BG.length] ?? 'bg-chart-1',
                )}
              />
              <span className="min-w-0 truncate text-foreground-secondary">{segment.label}</span>
              <span className="ml-auto shrink-0 font-data font-medium text-heading">
                {formatSegment(segment, percentOf(segment.value, total))}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
